/**
 * BISI Engineering Website - Content Data
 *
 * All website content. In production, this would come from an API or CMS.
 *
 * SECTIONS (in order):
 *   companyInfo, stats          - Company info & key metrics
 *   services, capabilities      - Service offerings
 *   servicesDetail              - Detailed service pages with images
 *   teamMembers, teamSections   - Team data
 *   orgStructure, mdMessage     - About page content
 *   clients, clientsList        - Client logos & descriptions
 *   testimonials                - Client quotes
 *   projects                    - Project portfolio
 *   galleryImages               - Gallery items
 *   certifications, policies   - Legal & compliance
 *   benefits, values, solutions - Home page content
 *
 * @see data/index.js - Central export
 */

import {
  CLIENT_LOGOS,
  FEATURED_IMAGES,
  PROJECT_IMAGES,
  SERVICE_IMAGES,
  TEAM_IMAGES,
} from '../constants/images';
import { getGalleryCollectionImages, getGalleryProjects } from '../lib/imageLoader';

// =============================================================================
// COMPANY INFORMATION
// =============================================================================

export const companyInfo = {
  name: "BISI Engineering",
  tagline: "27+ Years of Excellence",
  description: "Specializing in erection, installation, commissioning, operation, maintenance, up-gradation, and renovation of industrial projects and power plants across India.",
  website: "https://www.bisiengineering.in",
  phone: "+91 9437082081 / +91 9437071181",
  email: "bisiengineering@ymail.com",
  address: "Bisi House, Subhashree Vihar, Near Madanawati Public School, Ainthapali, Sambalpur, Pin - 768004, Odisha",
  businessHours: "Monday - Saturday: 9:00 AM - 6:00 PM",
  foundedYear: 2004,
};

// =============================================================================
// STATISTICS
// =============================================================================

export const stats = [
  { value: "27+", label: "Years Experience" },
  { value: "100+", label: "Happy Clients" },
  { value: "100+", label: "Projects Completed" },
  { value: "₹800Cr+", label: "Work Executed" },
  { value: "4300+", label: "Team Members" },
];

// =============================================================================
// SERVICES & CAPABILITIES
// =============================================================================

export const services = [
  {
    id: 1,
    icon: "Compass",
    title: "Erection & Installation",
    description: "Fabrication & Erection of Building Structures, Technological Structures for thermal power & steel plants.",
  },
  {
    id: 2,
    icon: "BarChart3",
    title: "Boiler Systems",
    description: "Erection of Boiler and Boiler Auxiliaries including ESP, Fans, Mills, Ducting etc.",
  },
  {
    id: 3,
    icon: "Target",
    title: "Maintenance & Overhauling",
    description: "Complete maintenance of Power Plants, Annual Overhauling of Boiler & Rotary Equipment.",
  },
];

export const capabilities = [
  {
    id: 1,
    title: "Erection & Installation",
    description: "Fabrication & Erection of Building Structures, Technological Structures for thermal power & steel plants.",
  },
  {
    id: 2,
    title: "Boiler Systems",
    description: "Erection of Boiler and Boiler Auxiliaries including ESP, Fans, Mills, Ducting etc.",
  },
  {
    id: 3,
    title: "Maintenance & Overhauling",
    description: "Complete maintenance of Power Plants, Annual Overhauling of Boiler & Rotary Equipment.",
  },
  {
    id: 4,
    title: "Pipeline Works",
    description: "Large Dia Cooling Water Pipelines, Underground & Inter Plant Pipelines, Steam Pipe lines.",
  },
  {
    id: 5,
    title: "Commissioning",
    description: "Equipment Erection & Commissioning of CRM, CSP, SMS, Wire Rod Mill, Bar Mill, Coal Washery & Power Plants.",
  },
  {
    id: 6,
    title: "Revamping & Renovation",
    description: "Revamping of CFBC, AFBC & WHR Boilers, Major modification of Boiler furnace & Pressure parts.",
  },
];

// =============================================================================
// SERVICES DETAIL (for Services page layout with key points & images)
// =============================================================================

export const servicesDetail = [
  {
    id: 1,
    number: "01",
    title: "Erection & Installation",
    description: "We develop and execute innovative structural concepts for thermal power and steel plants, ensuring each design is practical, robust, and built for longevity. From fabrication to erection of building and technological structures, we create custom solutions tailored to your project vision.",
    image: SERVICE_IMAGES.service1,
    keyPoints: [
      "Fabrication & erection of building structures",
      "Technological structures for thermal power plants",
      "Steel plant structural erection",
      "Compliance with industry standards & safety codes",
      "Coordination with OEMs & project managers",
    ],
  },
  {
    id: 2,
    number: "02",
    title: "Boiler Systems",
    description: "Our boiler erection services encompass complete installation of Boiler and Boiler Auxiliaries including ESP, Fans, Mills, Ducting and associated equipment. We ensure precision alignment, proper sequencing, and seamless integration with plant systems.",
    image: SERVICE_IMAGES.service2,
    keyPoints: [
      "Boiler & boiler auxiliaries erection",
      "ESP, Fans, Mills & ducting installation",
      "Pressure parts & non-pressure parts",
      "Testing and commissioning support",
      "Quality assurance & documentation",
    ],
  },
  {
    id: 3,
    number: "03",
    title: "Maintenance & Overhauling",
    description: "We provide complete maintenance of Power Plants and annual overhauling of Boiler & Rotary Equipment. Our experienced teams ensure minimal downtime, extended equipment life, and optimal operational efficiency for your critical assets.",
    image: SERVICE_IMAGES.service3,
    keyPoints: [
      "Complete power plant maintenance",
      "Annual boiler overhauling",
      "Rotary equipment servicing",
      "Preventive & predictive maintenance",
      "24/7 support for critical operations",
    ],
  },
  {
    id: 4,
    number: "04",
    title: "Pipeline Works",
    description: "From large diameter cooling water pipelines to underground and inter-plant pipelines, we deliver comprehensive piping solutions. Our expertise covers steam pipelines, process piping, and complex network installations across industrial facilities.",
    image: SERVICE_IMAGES.service4,
    keyPoints: [
      "Large dia cooling water pipelines",
      "Underground & inter-plant pipelines",
      "Steam pipeline fabrication & erection",
      "Hydro testing & commissioning",
      "Material handling & logistics",
    ],
  },
  {
    id: 5,
    number: "05",
    title: "Commissioning",
    description: "We handle equipment erection and commissioning of CRM, CSP, SMS, Wire Rod Mill, Bar Mill, Coal Washery and Power Plants. Our commissioning teams ensure smooth handover and operational readiness from installation to first production.",
    image: SERVICE_IMAGES.service5,
    keyPoints: [
      "Equipment erection & commissioning",
      "CRM, CSP, SMS & mill installations",
      "Coal washery & power plant setup",
      "Pre-commissioning checks & trials",
      "Operator training & documentation",
    ],
  },
  {
    id: 6,
    number: "06",
    title: "Revamping & Renovation",
    description: "Our revamping services include CFBC, AFBC & WHR Boilers, with major modifications to boiler furnace and pressure parts. We extend plant life, improve efficiency, and modernize existing installations with minimal disruption.",
    image: SERVICE_IMAGES.service6,
    keyPoints: [
      "CFBC, AFBC & WHR boiler revamping",
      "Boiler furnace modifications",
      "Pressure parts replacement",
      "Efficiency improvement upgrades",
      "Turnkey renovation solutions",
    ],
  },
];

// =============================================================================
// TEAM MEMBERS (legacy - kept for backwards compatibility)
// =============================================================================

export const teamMembers = [
  {
    id: 1,
    name: "Mr. Sukanta Kumar Bisi",
    role: "Founder & Managing Director",
    image: TEAM_IMAGES.mdPhoto,
    bio: "Cross-industry business leadership and strategic expertise with over 20 years of experience in Construction Projects, Manufacturing, Contracting, and Real Estate.",
  },
  {
    id: 2,
    name: "Mr. Uttam Kumar Sethi",
    role: "GM - Maintenance",
    image: TEAM_IMAGES.member2,
    bio: "Expert in power plant maintenance operations with extensive experience in industrial equipment servicing.",
  },
  {
    id: 4,
    name: "Mr. Ram Chandra Majhi",
    role: "GM - Operations",
    image: TEAM_IMAGES.member4,
    bio: "Operations management and execution excellence across multiple project sites.",
  },
  {
    id: 3,
    name: "Mr. Purandra Mahanta",
    role: "GM – Project",
    image: TEAM_IMAGES.member3,
    bio: "Leading large-scale industrial projects with proven track record of successful delivery.",
  },
  {
    id: 5,
    name: "Mr. Akshya Kumar Sahoo",
    role: "DGM - Maintenance",
    image: TEAM_IMAGES.member5,
    bio: "Deputy General Manager for maintenance operations with specialized expertise in boiler systems.",
  },
  {
    id: 6,
    name: "Mr. Amit Ku. Panda",
    role: "Manager – Finance",
    image: TEAM_IMAGES.member6,
    bio: "Financial strategy and management ensuring fiscal responsibility across all operations.",
  },
];

// =============================================================================
// TEAM SECTIONS (organized by category)
// =============================================================================

export const teamSections = [
  {
    id: "leadership",
    category: "LEADERSHIP",
    title: "Founder & Managing Director",
    description: null,
    members: [
      {
        id: 1,
        name: "Mr. Sukanta Kumar Bisi",
        role: "Founder & Managing Director",
        image: TEAM_IMAGES.mdPhoto,
        bio: "Cross-industry business leadership and strategic expertise",
      },
    ],
  },
  {
    id: "management",
    category: "MANAGEMENT",
    title: "Admin and Management Team",
    description: "Experienced professionals delivering excellence in industrial engineering and project management.",
    members: [
      { id: 2, name: "Mr. Uttam Kumar Sethi", role: "GM, Maintenance", image: TEAM_IMAGES.member2, bio: "Expert in power plant maintenance operations" },
      { id: 4, name: "Mr. Ram Chandra Majhi", role: "GM, Operations", image: TEAM_IMAGES.member4, bio: "Operations management and execution excellence" },
      { id: 3, name: "Mr. Purandra Mahanta", role: "GM, Project", image: TEAM_IMAGES.member3, bio: "Leading large-scale industrial projects" },
      { id: 5, name: "Mr. Akshya Kumar Sahoo", role: "DGM, Maintenance", image: TEAM_IMAGES.member5, bio: "Deputy General Manager for maintenance operations" },
      { id: 6, name: "Mr. Amit Ku. Panda", role: "Manager, Finance", image: TEAM_IMAGES.member6, bio: "Financial strategy and management" },
      { id: 7, name: "Mr. Jitendra Kumar Sethi", role: "Senior Accountant", image: TEAM_IMAGES.member1, bio: "Senior accounting and financial operations" },
      { id: 8, name: "Mr. Rajesh Naik", role: "Manager, HR / Admin / Tax", image: TEAM_IMAGES.member1, bio: "Human resources, administration and tax management" },
      { id: 9, name: "Mr. Pullak Kumar Dalai", role: "Manager, Liaisoning / TPI", image: TEAM_IMAGES.member1, bio: "Liaisoning and third-party inspection coordination" },
      { id: 10, name: "Mr. Nihar Ranjan Pradhan", role: "Purchase / Procurement Head", image: TEAM_IMAGES.member1, bio: "Leading procurement and purchase operations" },
      { id: 11, name: "Mr. Nilachal Pradhan", role: "Assistant to Purchase/Procurement Head", image: TEAM_IMAGES.member1, bio: "Supporting procurement and purchase operations" },
    ],
  },
  {
    id: "operations-site",
    category: "OPERATIONS",
    title: "Site Executives",
    description: "Ensuring smooth operations across all project locations: on-site coordination, resource management, and quality standards.",
    members: [
      { id: 12, name: "Mr. Jagannath Behera", role: "Site Executive (AOH), Hindalco", image: TEAM_IMAGES.member1, bio: "Annual overhauling site executive" },
      { id: 13, name: "Mr. Santosh Dash", role: "Site Executive, JSPL", image: TEAM_IMAGES.member1, bio: "Jindal Steel & Power Limited" },
      { id: 14, name: "Mr. Gayadhar Naik", role: "Site Executive, Shyam Metalic", image: TEAM_IMAGES.member1, bio: "Site execution and coordination" },
      { id: 15, name: "Mr. Sk Asmanuddin", role: "Site Executive, Rungta Sons", image: TEAM_IMAGES.member1, bio: "Site execution and coordination" },
      { id: 16, name: "Mr. Kishore Kumar Behera", role: "Site Executive, Operations", image: TEAM_IMAGES.member1, bio: "Site executive for operations" },
      { id: 17, name: "Mr. S. K. Gulam Nazif", role: "Site Executive, Maintenance (Hindalco)", image: TEAM_IMAGES.member1, bio: "Maintenance execution at Hindalco" },
      { id: 18, name: "Mr. Achuyatanand Das", role: "Site Executive", image: TEAM_IMAGES.member1, bio: "Project execution support" },
      { id: 19, name: "Mr. Kabiraj Suna", role: "Site Executive, ACC Cement", image: TEAM_IMAGES.member1, bio: "Site executive at ACC Cement" },
    ],
  },
  {
    id: "operations-assistant",
    category: "OPERATIONS",
    title: "Assistant Site Executives",
    description: "Supporting daily operations, quality control, and team coordination.",
    members: [
      { id: 20, name: "Mr. Mohan Patel", role: "Asst. Site Executive, JSW", image: TEAM_IMAGES.member1, bio: "Assistant site executive at JSW" },
      { id: 21, name: "Mr. Subasish Sethi", role: "Asst. Site Executive, Shyam Metalic", image: TEAM_IMAGES.member1, bio: "Assistant site executive at Shyam Metalic" },
      { id: 22, name: "Mr. Biswa Meher", role: "Asst. Site Executive, Aditya Birla", image: TEAM_IMAGES.member1, bio: "Assistant site executive at Aditya Birla" },
      { id: 23, name: "Mr. Salil Kumar Behera", role: "Asst. Site Executive (AOH), Hindalco", image: TEAM_IMAGES.member1, bio: "Assistant executive for annual overhauling at Hindalco" },
    ],
  },
  {
    id: "support",
    category: "SUPPORT",
    title: "Office Staff",
    description: "Providing documentation, coordination, and smooth communication across departments.",
    members: [
      { id: 24, name: "Mr. Ajay Kumar Pradhan", role: "Office Assistant", image: TEAM_IMAGES.member1, bio: "Office administration and support" },
      { id: 25, name: "Mr. Manchan Bariha", role: "Office Assistant", image: TEAM_IMAGES.member1, bio: "Office administration and support" },
    ],
  },
];

// =============================================================================
// ORGANIZATIONAL STRUCTURE
// =============================================================================

export const orgStructure = [
  {
    level: "Executive Leadership",
    members: ["Managing Director"],
  },
  {
    level: "General Managers",
    members: ["GM - Maintenance", "GM - Project", "GM - Operations"],
  },
  {
    level: "Department Managers",
    members: ["Manager Finance", "Manager HR/Admin", "Manager Contract & Commercial", "Manager HSE", "Manager QA/QC"],
  },
  {
    level: "Site Operations",
    members: ["Site Executives", "Assistant Site Executives", "Office Staff"],
  },
];

export const orgStructureCards = [
  { id: 1, division: "Executive Leadership", role: "Managing Director", description: "Dedicated team ensuring operational excellence and strategic growth." },
  { id: 2, division: "Maintenance Division", role: "General Manager (Maintenance)", description: "Dedicated team ensuring operational excellence and strategic growth." },
  { id: 3, division: "Projects Division", role: "General Manager (Project)", description: "Dedicated team ensuring operational excellence and strategic growth." },
  { id: 4, division: "HR & Administration", role: "Manager (HR & Admin)", description: "Dedicated team ensuring operational excellence and strategic growth." },
  { id: 5, division: "Finance & Accounts", role: "Manager Finance", description: "Dedicated team ensuring operational excellence and strategic growth." },
  { id: 6, division: "Contract & Commercial", role: "Manager Contract & Commercial", description: "Dedicated team ensuring operational excellence and strategic growth." },
  { id: 7, division: "Quality Assurance", role: "Manager Welding, QA/QC", description: "Dedicated team ensuring operational excellence and strategic growth." },
  { id: 8, division: "Health, Safety & Environment", role: "Manager HSE", description: "Dedicated team ensuring operational excellence and strategic growth." },
];

// =============================================================================
// MD MESSAGE
// =============================================================================

export const mdMessage = {
  name: "Mr. Sukanta Kumar Bisi",
  role: "Managing Director",
  image: TEAM_IMAGES.mdPhoto,
  message: "Welcome to BISI Engineering. For over two decades, we have been committed to delivering excellence in every project we undertake.\n\nFounded in 2004, our journey began with a simple vision: to transform the industrial construction sector through innovation, integrity, and unwavering commitment to quality. Today, we stand proud as one of Odisha's leading mechanical contracting companies.\n\nWith more than 20 years of business experience across diverse sectors including Construction Projects, Manufacturing, Contracting, and Real Estate, I have personally overseen our growth into a trusted partner for India's leading industrial giants.\n\nWe have had the privilege of working with prominent companies such as Hindalco Industries Limited, NTPC, BHEL, Vedanta, Jindal Steel & Power, Bhushan Power & Steel, and many more. Each project has strengthened our expertise and deepened our commitment to excellence.\n\nOur team of highly professional and experienced construction managers, engineers, and technical staff form the backbone of our success. We believe that every structure we build is a testament to our dedication to excellence.\n\nThank you for considering BISI Engineering for your next project. We look forward to building your vision together.",
};

// =============================================================================
// CLIENTS
// =============================================================================

export const clients = [
  { id: 1, name: "NTPC", logo: CLIENT_LOGOS.ntpc },
  { id: 2, name: "BHEL", logo: CLIENT_LOGOS.bhel },
  { id: 3, name: "OPGC", logo: CLIENT_LOGOS.opgc },
  { id: 4, name: "Jindal Steel", logo: CLIENT_LOGOS.jspl },
  { id: 5, name: "Hindalco", logo: CLIENT_LOGOS.hindalco },
  { id: 6, name: "Vedanta", logo: CLIENT_LOGOS.vedanta },
];

export const clientsList = [
  {
    id: 1,
    name: "NTPC Limited",
    industry: "Power Sector",
    logo: CLIENT_LOGOS.ntpc,
    description: "India's largest energy conglomerate with expertise in power generation across multiple fuel sources.",
  },
  {
    id: 2,
    name: "BHEL",
    industry: "Power Sector",
    logo: CLIENT_LOGOS.bhel,
    description: "Bharat Heavy Electricals Limited - India's largest power generation equipment manufacturer.",
  },
  {
    id: 3,
    name: "OPGC",
    industry: "Power Sector",
    logo: CLIENT_LOGOS.opgc,
    description: "Odisha Power Generation Corporation - Major power utility company in Odisha.",
  },
  {
    id: 4,
    name: "Aditya Aluminium",
    industry: "Aluminium & Mining",
    logo: CLIENT_LOGOS.aditya,
    description: "Aditya Birla Group aluminium company with major operations in Odisha.",
  },
  {
    id: 5,
    name: "Ambuja Cements",
    industry: "Cement",
    logo: CLIENT_LOGOS.ambuja,
    description: "Leading cement manufacturer and part of the global building materials industry.",
  },
  {
    id: 6,
    name: "ACC Limited",
    industry: "Cement",
    logo: CLIENT_LOGOS.acc,
    description: "One of India's largest cement producers with a legacy of excellence.",
  },
  {
    id: 7,
    name: "Jindal Steel & Power Limited",
    industry: "Steel & Metals",
    logo: CLIENT_LOGOS.jspl,
    description: "Leading Indian steel and power company with integrated steel plants across India.",
  },
  {
    id: 8,
    name: "Jindal Stainless Limited",
    industry: "Steel & Metals",
    logo: CLIENT_LOGOS.jsl,
    description: "India's largest stainless steel manufacturer with global presence.",
  },
  {
    id: 9,
    name: "Bhushan Power & Steel Limited",
    industry: "Steel & Metals",
    logo: CLIENT_LOGOS.bpsl,
    description: "Major steel manufacturing company with state-of-the-art facilities.",
  },
  {
    id: 10,
    name: "Aryan Ispat & Power",
    industry: "Steel & Metals",
    logo: CLIENT_LOGOS.aryan,
    description: "Integrated steel and power company with diversified operations.",
  },
  {
    id: 11,
    name: "Bhavani Industries",
    industry: "Steel & Metals",
    logo: CLIENT_LOGOS.bhavani,
    description: "Steel and industrial manufacturing company.",
  },
  {
    id: 12,
    name: "Spiegel",
    industry: "Steel & Metals",
    logo: CLIENT_LOGOS.spiegel,
    description: "Specialized in steel and industrial solutions.",
  },
  {
    id: 13,
    name: "Hindalco Industries Limited",
    industry: "Aluminium & Mining",
    logo: CLIENT_LOGOS.hindalco,
    description: "Aditya Birla Group company and one of the largest aluminium producers globally.",
  },
  {
    id: 14,
    name: "Vedanta Aluminium",
    industry: "Aluminium & Mining",
    logo: CLIENT_LOGOS.vedanta,
    description: "One of the world's largest aluminium producers with operations in India.",
  },
  {
    id: 15,
    name: "FACOR",
    industry: "Ferro Alloys",
    logo: CLIENT_LOGOS.facor,
    description: "Ferro Alloys Corporation Limited - leading ferro alloys producer.",
  },
  {
    id: 16,
    name: "L&T Construction",
    industry: "Engineering & Construction",
    logo: CLIENT_LOGOS.lt,
    description: "Larsen & Toubro - India's largest engineering and construction company.",
  },
  {
    id: 17,
    name: "FLSmidth",
    industry: "Engineering & Equipment",
    logo: CLIENT_LOGOS.flsmidth,
    description: "Global supplier of equipment and services to the cement and minerals industries.",
  },
  {
    id: 18,
    name: "Macawber Engineering",
    industry: "Engineering & Equipment",
    logo: CLIENT_LOGOS.macawber,
    description: "Specialists in pneumatic conveying and bulk material handling systems.",
  },
  {
    id: 19,
    name: "Mecgale Pneumatics",
    industry: "Engineering & Equipment",
    logo: CLIENT_LOGOS.mecgale,
    description: "Provider of pneumatic conveying and material handling solutions.",
  },
  {
    id: 20,
    name: "Gannon Dunkerley",
    industry: "Engineering & Construction",
    logo: CLIENT_LOGOS.gannon,
    description: "Established engineering and construction company with diverse capabilities.",
  },
  {
    id: 21,
    name: "Power Max",
    industry: "Power Sector",
    logo: CLIENT_LOGOS.powerMax,
    description: "Power generation and energy solutions company.",
  },
];

// =============================================================================
// TESTIMONIALS
// =============================================================================

export const testimonials = [
  {
    id: 1,
    name: "Project Manager",
    role: "NTPC Darlipali",
    image: CLIENT_LOGOS.ntpc,
    quote: "BISI Engineering delivered exceptional quality in our 2x800 MW boiler ESP project. Their technical expertise and commitment to timelines made them a valuable partner.",
  },
  {
    id: 2,
    name: "Operations Head",
    role: "Hindalco Industries",
    image: CLIENT_LOGOS.hindalco,
    quote: "Working with BISI for our annual overhauling has been a great experience. Their team's professionalism and attention to safety standards is commendable.",
  },
  {
    id: 3,
    name: "Site Director",
    role: "Bhushan Power & Steel",
    image: CLIENT_LOGOS.bpsl,
    quote: "The blast furnace piping project was completed with precision and within budget. BISI Engineering's expertise in industrial construction is truly remarkable.",
  },
  {
    id: 4,
    name: "Plant Manager",
    role: "Aditya Aluminium Ltd",
    image: CLIENT_LOGOS.aditya,
    quote: "BISI handled the 6x150 MW boiler erection project flawlessly. Their experienced team and quality-focused approach made all the difference.",
  },
];

// =============================================================================
// PROJECTS – Ongoing & Recent (2025–2026) first, then Past Major Projects
// =============================================================================

// Project categories = Services page categories (same as servicesDetail titles) for consistent filtering
// Use categories array; projects with erection/installation keywords also tagged "Erection & Installation"
// Image: newest (first) → project1, oldest (last) → project14; continues cycling when adding more
const projectsRaw = [
  // ─── Ongoing & Recent Projects (2025 – 2026) ───
  { id: 1, section: 'ongoing', title: '127.5 TPH WHRB Boiler', client: 'Shyam Metalic – Sambalpur', categories: ['Boiler Systems', 'Erection & Installation'], description: '127.5 TPH WHRB boiler erection and commissioning work. Structure, pressure part, ducting and rotary equipment.', results: ['2025–2026', 'Ongoing'] },
  { id: 2, section: 'ongoing', title: '90 MW Power Plant – TG Auxiliaries', client: 'Shyam Metalic – Sambalpur', categories: ['Pipeline Works', 'Erection & Installation'], description: '90 MW power plant TG auxiliary steam piping work.', results: ['2025–2026', 'Ongoing'] },
  { id: 3, section: 'ongoing', title: '230 TPH CFBC Boiler House Piping', client: 'Shyam Metalic – Sambalpur', categories: ['Boiler Systems', 'Erection & Installation'], description: '230 TPH CFBC boiler house piping erection and commissioning work.', results: ['2025–2026', 'Ongoing'] },
  { id: 4, section: 'ongoing', title: '210 TPH CFBC Boiler Revamping', client: 'JSW BPSL – Sambalpur', categories: ['Revamping & Renovation'], description: '210 TPH CFBC boiler pressure part revamping work.', results: ['2025–2026', 'Ongoing'] },
  { id: 5, section: 'ongoing', title: '390 TPH CFBC Boiler Overhauling', client: 'JSW BPSL – Sambalpur', categories: ['Maintenance & Overhauling'], description: '390 TPH CFBC boiler complete overhauling work.', results: ['2025–2026', 'Ongoing'] },
  { id: 6, section: 'ongoing', title: '130 MW Power Plant – BOP Maintenance', client: 'JSW BPSL – Sambalpur', categories: ['Maintenance & Overhauling'], description: '130 MW power plant BOP maintenance work.', results: ['2025–2026', 'Ongoing'] },
  { id: 7, section: 'ongoing', title: '320 TPD ESP Revamping', client: 'JSW BPSL – Sambalpur', categories: ['Revamping & Renovation'], description: '320 TPD ESP revamping work at centre plant.', results: ['2025–2026', 'Ongoing'] },
  { id: 8, section: 'ongoing', title: '185 × 2 TPH CFBC Boilers', client: 'Rungta Steel & Power – Jharsuguda', categories: ['Revamping & Renovation'], description: '185 x 2 TPH CFBC boiler renovation work.', results: ['2025–2026', 'Ongoing'] },
  { id: 9, section: 'ongoing', title: '55 × 2 MW Power Plant', client: 'Rungta Steel & Power – Jharsuguda', categories: ['Commissioning', 'Erection & Installation'], description: '55 x 2 MW power plant steam piping erection and commissioning work.', results: ['2025–2026', 'Ongoing'] },
  { id: 10, section: 'ongoing', title: '135 × 6 MW Boilers', client: 'Vedanta – Jharsuguda', categories: ['Maintenance & Overhauling'], description: '135 x 6 MW boiler complete overhauling work.', results: ['2025–2026', 'Ongoing'] },
  { id: 11, section: 'ongoing', title: '155 × 3 TPH CFBC Boilers', client: 'Vedanta Facor – Bhadrak', categories: ['Revamping & Renovation'], description: '155 x 3 TPH CFBC boiler complete overhauling and pressure part coil replacement work.', results: ['2025–2026', 'Ongoing'] },
  { id: 12, section: 'ongoing', title: '336 TPH CFBC Boiler', client: 'JSW Steel (Monet) – Raigarh', categories: ['Revamping & Renovation'], description: '336 TPH CFBC boiler complete overhauling and pressure part coil replacement work.', results: ['2025–2026', 'Ongoing'] },
  { id: 13, section: 'ongoing', title: '425 × 4 TPH CFBC Boilers', client: 'JSPL – Anugul', categories: ['Maintenance & Overhauling'], description: '425 x 4 TPH CFBC boiler complete overhauling and non-pressure part work.', results: ['2025–2026', 'Ongoing'] },
  { id: 14, section: 'ongoing', title: '350 × 2 MW Boilers', client: 'JSW Utkal Energy – Jharsuguda', categories: ['Revamping & Renovation'], description: '350 x 2 MW boiler complete overhauling and pressure part coil replacement work.', results: ['2025–2026', 'Ongoing'] },
  { id: 15, section: 'ongoing', title: '155 × 9 TPH CFBC Boilers', client: 'Hindalco – Hirakud', categories: ['Revamping & Renovation'], description: '155 x 9 TPH CFBC boiler complete overhauling and pressure part coil replacement work.', results: ['2025–2026', 'Ongoing'] },
  { id: 16, section: 'ongoing', title: '467 MW Power Plant', client: 'Hindalco – Hirakud', categories: ['Maintenance & Overhauling'], description: '467 MW power plant TG and BOP equipment maintenance work.', results: ['2025–2026', 'Ongoing'] },
  { id: 17, section: 'ongoing', title: '900 MW Power Plant', client: 'Aditya Birla – Sambalpur', categories: ['Maintenance & Overhauling'], description: '900 MW power plant BOP equipment maintenance work.', results: ['2025–2026', 'Ongoing'] },
  { id: 18, section: 'ongoing', title: '150 × 6 MW Boilers (Annual)', client: 'Aditya Birla – Sambalpur', categories: ['Maintenance & Overhauling'], description: '150 x 6 MW power plant boiler complete overhauling work. (Annual)', results: ['2025–2026', 'Ongoing'] },
  { id: 19, section: 'ongoing', title: '350 TPD WHRB & ESP', client: 'Odisha Sponge – Keonjhar', categories: ['Boiler Systems', 'Erection & Installation'], description: '350 TPD WHRB and ESP erection and commissioning work.', results: ['2025–2026', 'Ongoing'] },
  { id: 20, section: 'ongoing', title: '50 MW Power Plant', client: 'ACC Ltd. – Bargarh', categories: ['Maintenance & Overhauling'], description: '50 MW power plant routine maintenance work of boiler and BOP equipment.', results: ['2025–2026', 'Ongoing'] },
  // ─── Past Major Projects ───
  { id: 21, section: 'past', title: '2 × 800 MW ESP Maintenance', client: 'NTPC – Darlipali', categories: ['Maintenance & Overhauling'], description: '2 x 800 MW ESP routine maintenance work.', results: ['2020–2023', 'Completed'] },
  { id: 22, section: 'past', title: '2 × 800 MW ESP Systems', client: 'NTPC – Darlipali', categories: ['Boiler Systems', 'Erection & Installation'], description: '2 x 800 MW ESP erection and commissioning work.', results: ['2017–2020', 'Completed'] },
  { id: 23, section: 'past', title: '3 × 180 MW Units', client: 'NTPC TTPS – Talcher', categories: ['Maintenance & Overhauling'], description: '3 x 180 MW boiler and rotary equipment routine maintenance work.', results: ['2015–2020', 'Completed'] },
];

const getProjectImageByPosition = (index, total) => {
  const num = total <= 1 ? 1 : Math.min(14, Math.max(1, 1 + Math.round((index / (total - 1)) * 13)));
  return PROJECT_IMAGES[`project${num}`];
};

export const projects = projectsRaw.map((p, i) => ({
  ...p,
  image: getProjectImageByPosition(i, projectsRaw.length),
}));

// =============================================================================
// FEATURED SECTION (Republic Day Celebration)
// =============================================================================

export const featuredSection = {
  title: 'Happy Republic Day 🇮🇳',
  message:
    'A proud and joyful celebration as our Managing Director hoisted the national flag, followed by cheerful moments of togetherness with our team. United in spirit, we celebrated the values of freedom, unity, and responsibility that define our nation.',
  images: [
    { id: 'f1', title: 'Republic Day Celebration', category: 'Events', image: FEATURED_IMAGES.featured1 },
    { id: 'f2', title: 'Republic Day Celebration', category: 'Events', image: FEATURED_IMAGES.featured2 },
    { id: 'f3', title: 'Republic Day Celebration', category: 'Events', image: FEATURED_IMAGES.featured3 },
    { id: 'f4', title: 'Republic Day Celebration', category: 'Events', image: FEATURED_IMAGES.featured4 },
    { id: 'f5', title: 'Republic Day Celebration', category: 'Events', image: FEATURED_IMAGES.featured5 },
  ],
};

// =============================================================================
// GALLERY (from gallery/project folders - excludes Featured)
// =============================================================================

export const galleryProjects = getGalleryProjects();
export const galleryImages = getGalleryCollectionImages();

// =============================================================================
// CERTIFICATIONS – links to PDFs in /public/certifications/ or comingSoon
// =============================================================================

export const certifications = [
  { id: 1, name: "ISO 9001:2015", description: "Quality Management System", pdfUrl: null, comingSoon: true },
  { id: 2, name: "ISO 45001:2018", description: "Occupational Health & Safety", pdfUrl: null, comingSoon: true },
  { id: 3, name: "Udyam Registration", description: "MSME Udyam Registration Certificate", pdfUrl: "/certifications/udyam-registration-certificate.pdf", comingSoon: false },
  { id: 4, name: "GST Registration", description: "Goods and Services Tax Registration", pdfUrl: "/certifications/gst-registration.pdf", comingSoon: false },
  { id: 5, name: "EPF Registration", description: "Employees' Provident Fund Registration", pdfUrl: "/certifications/epf-registration.pdf", comingSoon: false },
  { id: 6, name: "ESI Registration", description: "Employees' State Insurance Registration", pdfUrl: "/certifications/esi-registration.pdf", comingSoon: false },
  { id: 7, name: "Boiler Erector License", description: "License for erection of boilers", pdfUrl: "/certifications/boiler-erector-license.pdf", comingSoon: false },
  { id: 8, name: "Boiler Repairer License", description: "License for boiler repair works", pdfUrl: "/certifications/boiler-repairer-license.pdf", comingSoon: false },
  { id: 9, name: "OS/CE License", description: "Statutory license (Odisha State)", pdfUrl: "/certifications/os-ce-license.pdf", comingSoon: false },
];

// =============================================================================
// COMPANY POLICIES
// =============================================================================

export const policies = [
  {
    id: 1,
    title: "Conduct of Business",
    description: "All business shall be conducted in an efficient, fair, and ethical manner. We respect social norms and environmental standards.",
  },
  {
    id: 2,
    title: "Workplace Safety",
    description: "Safety of people in the workplace is our primary concern. We ensure compliance with all applicable health and safety norms.",
  },
  {
    id: 3,
    title: "Compliance & Standards",
    description: "We ensure compliance with applicable accounting standards and tax laws when reporting financial data.",
  },
  {
    id: 4,
    title: "Equal Opportunity",
    description: "We provide a work environment free from discrimination. Merit is the sole criterion for performance assessment.",
  },
  {
    id: 5,
    title: "Rules & Regulations",
    description: "All members must adhere to rules and regulations with strict compliance to code of conduct.",
  },
  {
    id: 6,
    title: "Client Responsibility",
    description: "Complete information about projects, services, and safety requirements are disclosed to clients before awarding a job.",
  },
];
